import { randomHex } from './ids';
import type {
  BeforeTrace,
  CallTrace,
  GlobalTrace,
  HeaderSource,
  RequestOptions,
  Trace,
  TraceContext,
} from './types';

/**
 * The two headers this library can add, and where their values come from.
 *
 * `x-request-id` names the REQUEST on the wire. `traceparent` names a span in a
 * W3C trace. Two questions, which is why `trace` has four independent states
 * rather than a ladder: a caller who only needs their nginx logs to line up asks
 * for the first, a caller inside a traced system asks for the second (TR1-TR4).
 *
 * NOTHING is added unless asked. A process that already runs a tracer injects
 * `traceparent` from underneath -- MEASURED, `dd-trace` wraps `globalThis.fetch`
 * -- so a library that also injected would give one answer two origins (TR9).
 */

/**
 * `<version>-<32 hex trace-id>-<16 hex span-id>-<2 hex flags>`, and whatever a
 * later version appends after that.
 *
 * The trailing group is not laxness, it is the shape the recommendation asks a
 * reader to accept: the flags must be followed by the end of the value or by a
 * dash, and what comes after the dash belongs to a version we do not know. Which
 * versions may actually carry it is decided in `isContinuable`, not here (TR12).
 */
const TRACEPARENT =
  /^(?<version>[0-9a-f]{2})-(?<traceId>[0-9a-f]{32})-(?<spanId>[0-9a-f]{16})-(?<flags>[0-9a-f]{2})(?<rest>-.*)?$/u;

/**
 * The highest version this library understands, and therefore the one it WRITES.
 *
 * It reads more than it writes. A higher version is continued by its known
 * prefix and written back as this one, because that is what keeps a trace whole
 * on the day a new version appears rather than breaking it silently (TR12).
 */
const VERSION = '00';

/** Reserved by the recommendation, so never a parent to continue (TR22). */
const RESERVED = 'ff';

/**
 * What a request id may be made of, and how long, when this library FORWARDS one
 * (TR17).
 *
 * It once had a second consumer that stripped rather than refused, so that a
 * reported id would fit a log field. That one is gone: what is reported is now
 * literally what went on the wire, because shaping it mapped two different
 * requests onto one id (ER64).
 */
const ID_CHARS = 'A-Za-z0-9._-';

/** Datadog's `http.request_id` holds no more than this (ER64). */
const ID_MAX = 64;

/** What may go back OUT unchanged, all or nothing. */
const FORWARDABLE = new RegExp(`^[${ID_CHARS}]{1,${ID_MAX}}$`, 'u');

/**
 * An inherited value, or nothing when it cannot safely go back out.
 *
 * MEASURED, and this is why it exists: `new Headers().set('x-request-id', 'a\nb')`
 * throws `TypeError: invalid header value`. These values come off somebody
 * else's request, they are put on ours AFTER the pipeline's own `toHeaders`
 * step, and so a newline from an upstream would end the call with a bare
 * platform error rather than one of this library's (TR17).
 *
 * The id is narrowed to what `ER64` says it is anyway -- at most 64 characters
 * from `[A-Za-z0-9._-]` -- and anything with a character outside that is not
 * repaired but dropped. A half-repaired id correlates to nothing, and inventing
 * one would claim a request that never existed.
 */
export function forwardableId(value: string | undefined): string | undefined {
  if (value === undefined) return undefined;
  return FORWARDABLE.test(value) ? value : undefined;
}

/**
 * `tracestate` verbatim, or nothing when it is not safe to forward.
 *
 * Unread on purpose: the W3C recommendation says a system that does not
 * understand a vendor's entry passes it on untouched. What is checked is only
 * that it CAN be a header value: printable ASCII, which rules out the newline
 * that would end the call with a bare platform error (`TR17`).
 *
 * NOT capped at 512. That number is a floor in 3.3.1.5 -- "Vendors SHOULD
 * propagate at least 512 characters of a combined header" -- and this code read
 * it as a ceiling and dropped the whole header above it. A vendor that does have
 * a size limit "MUST truncate whole entries" rather than discard the lot; this
 * library has no limit of its own, so it never needs to truncate and does not
 * (`TR36`).
 */
function usableStateOf(value: string | undefined): string | undefined {
  if (value === undefined) return undefined;
  return /^[ -~]+$/u.test(value) ? value : undefined;
}

/**
 * All-zero is what the recommendation says is invalid, for both ids.
 *
 * Read side only, and deliberately: the rule is written for a RECEIVER, which
 * must ignore such a parent. It says nothing to a sender, and the odds of
 * `randomHex` producing one are 2^-64 on the span, so a guard on the write side
 * would be a branch no test could ever reach.
 */
function isZero(id: string): boolean {
  return /^0+$/u.test(id);
}

/** The groups of `TRACEPARENT` that decide whether a parent is continued. */
interface ParentGroups {
  rest: string | undefined;
  spanId: string | undefined;
  traceId: string | undefined;
  version: string | undefined;
}

/**
 * Whether a parsed parent is one this library may continue.
 *
 * Version `00` is the exact four-field form with nothing after it, because
 * within that version a fifth field does not exist. A HIGHER version is read for
 * its known prefix and whatever follows is ignored, which is what the W3C trace
 * context recommendation asks of a reader. `ff` stays reserved, and an all-zero
 * id is invalid in either field.
 *
 * THREE GROUNDS FOR REFUSING, and the parameter type suggests a fourth that
 * cannot happen. `version`, `traceId` and `spanId` are non-optional groups of
 * `TRACEPARENT`, so a successful `exec` gives all three -- the `undefined` guard
 * below is unreachable from the only call site, and is here because the
 * parameter is typed wider than that call site can deliver. Do not read it as
 * evidence that a group may go missing.
 */
function isContinuable({
  rest,
  spanId,
  traceId,
  version,
}: ParentGroups): boolean {
  if (version === undefined || traceId === undefined || spanId === undefined)
    return false;
  if (version === RESERVED) return false;
  if (version === VERSION && rest !== undefined) return false;
  return !isZero(traceId) && !isZero(spanId);
}

/**
 * The sampled bit, which is the only flag the recommendation gives meaning to
 * today. The rest of the byte is reserved, so it is read as one boolean rather
 * than kept as two hex characters a caller could put anything into.
 */
function sampledFrom(flags: string | undefined): boolean {
  return flags !== undefined && (Number.parseInt(flags, 16) & 1) === 1;
}

/**
 * A trace-id this library is willing to WRITE, or nothing.
 *
 * `traceFrom` refuses a malformed or all-zero one on the way in, but a
 * `TraceContext` is an exported structural interface, so a caller holding a
 * trace-id in memory can build one that never passed that reader. Checking here
 * as well is what makes the rule a property of the OUTPUT rather than of one
 * route towards it (TR17).
 */
function usableTraceIdOf(value: string | undefined): string | undefined {
  if (value === undefined) return undefined;
  return /^[0-9a-f]{32}$/u.test(value) && !isZero(value) ? value : undefined;
}

/**
 * One header VALUE folded to a usable string, or nothing.
 *
 * The one place an INCOMING value is decided, which is why it is its own
 * function rather than a `??` at each call site. `HeaderLike` admits a bare
 * string, an array of them, `undefined` and `[]`, and `Headers.get` adds `null`;
 * each of those used to be folded somewhere else or not at all (TR35).
 *
 * `sentTraceOf` and `traceOf` read `Headers` without coming through here, and
 * deliberately: those are the headers this library just WROTE, so there is no
 * framework's idea of an absent header to decide about.
 */
function firstOf(value: unknown): string | undefined {
  // An array folds to its FIRST element. The branch is here because `HeaderLike`
  // admits `string[]` and a caller may hand one over -- NOT because the ordinary
  // Node path produces one for these three names. MEASURED against a real server
  // with two `traceparent` headers on one request: `req.headers.traceparent` is a
  // single comma-joined string, and `set-cookie` is the one name that arrives as
  // an array. Arrays for other names come off `req.headersDistinct`, which a
  // caller opts into, or off a framework that builds them itself.
  //
  // WHICH element is a choice, on the reading that the edge wrote the first and
  // the hops behind it appended. Guarded by the oracle block, which generates an
  // array of two differing elements for that purpose (TR35).
  const first: unknown = Array.isArray(value) ? value[0] : value;
  // THE EMPTY STRING IS NO VALUE, and that is a DECISION rather than a
  // consequence: a framework that hands an absent header over as `''` carries no
  // header. It lives HERE, at the one place a value is folded, so that every
  // caller answers alike -- `''`, `['']` and a `Headers` holding an empty value
  // are one question, and two of the three used to be answered elsewhere and the
  // third not at all (TR35).
  return typeof first === 'string' && first !== '' ? first : undefined;
}

/**
 * One header's value, read case-insensitively from the keys the source
 * enumerates, and under the exact lower-case name when it enumerates none.
 *
 * @remarks
 * A Fetch `Headers` and Node's plain record are both everyday, and the call site
 * is a handler that has one or the other rather than a choice (TR10). `Headers`
 * folds case itself; a record keeps whatever its framework wrote, which is what
 * the loop below is for.
 *
 * EXPORTED FOR ITS TESTS, and not part of the public surface -- `index.ts` does
 * not re-export it, the way `flightOf` and `joinFlight` are reached from their
 * own tests and no further.
 *
 * The lookup earns that seam because `traceFrom` shows the difference only when
 * a source spells the name TWICE. Under ONE spelling lookup and validation mask
 * each other: a value this returns meets `TRACEPARENT.exec`, `usableStateOf` or
 * `forwardableId` next, all three refuse an empty string, and a lookup that
 * loses a header then reads exactly like a header that was never there. Under
 * TWO the difference is plain for all three headers -- MEASURED -- because the
 * rules here also decide whether the search runs ON past a matching name. No row
 * carried a second spelling before `TR35`, which is how the empty-value case got
 * in unnoticed.
 *
 * @param source The incoming headers.
 * @param name The header to read, spelled in lower case.
 * @returns The first usable value under an enumerable key that folds to `name`;
 *   failing that, the value under a key spelled exactly `name`, enumerable or
 *   not; failing that, `undefined`. So `undefined` means no USABLE value was
 *   reached, which is not the same as the source carrying none: a non-enumerable
 *   own key masks an inherited one of the same name and is itself out of the
 *   loop's reach under any other spelling.
 */
export function headerOf(
  source: HeaderSource,
  name: string,
): string | undefined {
  try {
    // Through `firstOf` rather than `?? undefined`, so that one rule about what a
    // value IS covers both branches. `Headers.get` answers `null` for a name it
    // does not hold, which folds to nothing, and an empty value folds the same way
    // a record's `''` does -- MEASURED that `new Headers({ traceparent: '' })`
    // keeps the name and reports `''`, so without this the two branches disagreed
    // about the one shape the record branch exists to decide (TR35).
    if (source instanceof Headers) return firstOf(source.get(name));
    // A plain record differs from `Headers` in that `Headers` folds case for you
    // and an object keeps whatever its framework wrote, so folding it here is not
    // politeness: the W3C trace context recommendation says a vendor must expect
    // the name in any case, and a record spelled `Traceparent` would otherwise
    // lose the trace in silence while `Headers` with the same spelling kept it.
    //
    // `for...in` rather than `Object.entries`, so that this and the direct read
    // below see the same source. Own enumerable keys only would have meant an
    // inherited `traceparent` is found and an inherited `Traceparent` is not,
    // which is the same silent loss one layer deeper.
    //
    // This loop DECIDES and the read below is its fallback, in that order,
    // because that read walks the whole prototype chain. With it running first an
    // inherited `traceparent` beat an own `Traceparent` -- MEASURED -- so the
    // nearest layer lost to the exact lower-case spelling in any layer, which is
    // the same silent loss again, one spelling across. This way round the nearest
    // layer wins instead, and that rests on `for...in` visiting own keys before
    // inherited ones.
    //
    // WHICH ECMAScript DOES NOT PROMISE. `EnumerateObjectProperties` fixes the
    // shadowing -- a name already produced is not produced again, and
    // `[[Enumerable]]` does not enter into whether it counts as produced -- but
    // of the order it says only that it "is not specified". Own-before-inherited
    // is what the common engines do and what `proposal-for-in-order` is still in
    // the process of writing down. MEASURED true on node v22.21.1; an accepted
    // dependency on the engine, stated here rather than left to be discovered,
    // and the one axis the oracle cannot contradict the code on because it
    // encodes the same assumption (TR35).
    for (const key in source) {
      if (key.toLowerCase() !== name) continue;
      let folded;
      try {
        folded = firstOf(source[key]);
      } catch {
        // A key that resists being read carries nothing, and the search goes ON.
        // The outer guard alone would answer `undefined` for the whole source, so
        // a hostile `Traceparent` getter beside a perfectly good `traceparent`
        // cost the trace -- MEASURED in five shapes. `TR16` asks that a throwing
        // read never end the call; it does not ask that it end the lookup.
        continue;
      }
      // Only a USABLE value ends the search. `HeaderLike` admits `undefined`,
      // `[]`, `''` and `['']`, and stopping at the name alone would let
      // `{ Traceparent: undefined, traceparent: <parent> }` lose a trace it
      // plainly carries -- MEASURED. What counts as usable is `firstOf`'s to say,
      // for every source shape at once.
      if (folded !== undefined) return folded;
    }
    // The fallback, REACHED ONLY when the loop settled on nothing usable, and
    // what it is for: a non-enumerable key is invisible to the loop, and this
    // read finds one spelled exactly `name`. It is a fallback and not a second
    // opinion -- where the loop did find a usable value, a non-enumerable key
    // spelled exactly `name` is never consulted. `Object.entries` skipped a
    // non-enumerable key just as thoroughly, so that much did not regress.
    //
    // What the loop DOES newly reach is a capitalised name on a polluted
    // `Object.prototype` -- the read is case-sensitive and `Object.entries`
    // stopped at own keys, so neither saw one, and a request carrying no trace at
    // all now inherits that one. Skipping `Object.prototype` would close it and is
    // a separate decision, not taken here (TR35).
    return firstOf(source[name]);
  } catch {
    // Any read here MAY run somebody else's code: a getter, a `Proxy` trap, an
    // overridden `Headers.get`. `traceFrom` promises never to throw, and an
    // absolute is worth only what enforces it, so a source that resists being
    // read is treated as a source that carries nothing (TR16).
    return undefined;
  }
}

/** Whether a value is a shape `headerOf` can read at all. */
function isHeaderSource(source: unknown): source is HeaderSource {
  return typeof source === 'object' && source !== null;
}

/**
 * The trace an incoming request carries, or `undefined` when it carries none.
 *
 * @remarks
 * Hand it whatever your framework gave you: a Fetch `Headers`, or the plain
 * record Node builds. What comes back goes straight into `trace` on a call, and
 * that call then continues the same trace instead of starting one.
 *
 * IT NEVER THROWS. Reading somebody else's headers happens in a handler that
 * usually has no error handling of its own, so anything unusable -- a
 * malformed value, the reserved version `ff`, or all-zero ids -- yields
 * `undefined` and the call falls back to whatever `trace` says. An unknown
 * version such as `42` is CONTINUED, not refused: this library reads more
 * than it writes, following the known prefix and writing the `traceparent` it
 * sends on under the version it understands. An `x-request-id` alone, with no usable
 * `traceparent`, yields `{ requestId }`.
 *
 * @param
 * source The incoming headers. Typed `unknown` because this reads data from
 * outside the type system; `HeaderSource` documents the shapes it understands.
 *
 * @returns
 * The trace to continue, or `undefined` when there is nothing to continue.
 *
 * @throws
 * Nothing, ever, and that is the point rather than an omission. The call site
 * is usually a request handler with no error handling of its own, so reading
 * somebody else's headers may not be able to end a call.
 *
 * @example
 * ```ts
 * // In a request handler: pass the caller's trace on to what you call next.
 * const inherited = traceFrom(req.headers);
 * const teams = await request<Team[]>({ trace: inherited, url: '/api/teams' });
 * ```
 */
export function traceFrom(source: unknown): TraceContext | undefined {
  if (!isHeaderSource(source)) return undefined;

  const requestId = forwardableId(headerOf(source, 'x-request-id'));
  const parent = TRACEPARENT.exec(headerOf(source, 'traceparent') ?? '');
  const groups = parent?.groups;

  if (
    groups === undefined ||
    !isContinuable({
      rest: groups.rest,
      spanId: groups.spanId,
      traceId: groups.traceId,
      version: groups.version,
    })
  ) {
    // No usable parent. A bare `x-request-id` is still a trace worth carrying:
    // it is what ties this call to the request that caused it.
    return requestId === undefined ? undefined : { requestId };
  }

  return {
    requestId,
    // Passed on unchanged. We export nothing, so we take no sampling decision,
    // and the recommendation says a component that defers passes the flag on
    // rather than answering for the one that did decide (TR21).
    sampled: sampledFrom(groups.flags),
    spanId: groups.spanId,
    traceId: groups.traceId,
    traceState: usableStateOf(headerOf(source, 'tracestate')),
  };
}

/**
 * The headers one call should send, given what it asked for.
 *
 * A fresh id per header it writes. The request id is 32 hex, which is both
 * nginx's `$request_id` shape and a valid W3C trace-id, so one value reads in
 * both worlds (TR7).
 *
 * `sampled` is off on anything this library starts. We export no spans, so the
 * flag would promise there is something to fetch at the other end (TR8).
 */
function buildTraceHeaders(
  mode: CallTrace,
  callId: string,
  traceId: string,
): Record<string, string> {
  if (mode === false) return {};
  if (typeof mode === 'object') return continuedHeadersOf(mode);

  const wantsId = mode === true || mode === 'request-id';
  const wantsTrace = mode === true || mode === 'trace';
  return {
    // The CALL's own id, not a second one. When this library starts the
    // request, the call IS the request, and two ids for one thing would be two
    // names a reader has to learn to tell apart (ER65).
    ...(wantsId ? { 'x-request-id': callId } : {}),
    // The trace-id comes from the CALL, minted once beside the `callId`, while
    // the span is fresh here. This function runs once per ATTEMPT, so minting
    // the trace-id here too would make every retry its own unrelated trace
    // instead of a sibling span under one (TR19).
    ...(wantsTrace
      ? { traceparent: `${VERSION}-${traceId}-${randomHex(8)}-00` }
      : {}),
  };
}

/**
 * The same trace, one span further along.
 *
 * The trace-id and `tracestate` travel unchanged; the span-id is fresh, because
 * two spans claiming the same id is what a trace viewer cannot untangle (TR5).
 * A context that carries only a request id continues just that.
 */
function continuedHeadersOf(context: TraceContext): Record<string, string> {
  const requestId = forwardableId(context.requestId);
  const traceId = usableTraceIdOf(context.traceId);
  const traceState = usableStateOf(context.traceState);
  return {
    ...(requestId === undefined ? {} : { 'x-request-id': requestId }),
    ...(traceId === undefined
      ? {}
      : {
          traceparent: `${VERSION}-${traceId}-${randomHex(8)}-${
            context.sampled === true ? '01' : '00'
          }`,
          ...(traceState === undefined ? {} : { tracestate: traceState }),
        }),
  };
}

/**
 * The trace headers this call would send, or none.
 *
 * The values one call would send, computed once.
 */
export function headersForTrace(
  mode: CallTrace | undefined,
  callId: string,
  traceId: string,
): Record<string, string> {
  return mode === undefined ? {} : buildTraceHeaders(mode, callId, traceId);
}

/**
 * Puts those headers on the call, without overruling the caller.
 *
 * A header the caller wrote is left alone: `TR6` is the whole rule and
 * `Headers.has` is the whole implementation of it.
 *
 * Separate from `headersForTrace` because the VALUES are needed one step earlier
 * than the headers are: the coalescing key is built first, and `perTrace` keys
 * on what this call would send (`TR15`). Computing them twice would mint two
 * different ids and the key would describe a request that never went out.
 */
export function addTrace(
  headers: Headers,
  traced: Record<string, string>,
): void {
  for (const [name, value] of Object.entries(traced))
    if (!headers.has(name)) headers.set(name, value);
}

/**
 * The absence of a global trace, as a value the compiler will not narrow away.
 *
 * Same shape and same reason as `noTransport`: a plain `undefined` on the
 * right-hand side is narrowed to that literal type by control flow, and the
 * property would then refuse the state an app assigns to it later.
 */
export function noTrace(): GlobalTrace | undefined {
  return undefined;
}

/**
 * What this call traces: its own answer, then the process-wide one.
 *
 * The global arrives as an ARGUMENT rather than through an import, which is what
 * keeps this module a leaf -- the same arrangement `transportFor` uses, for the
 * same reason.
 *
 * A function global is asked HERE, once per call, which is the whole point of
 * allowing one: it can read whatever the framework holds for this request.
 */
export function traceFor(
  own: CallTrace | undefined,
  global: GlobalTrace | undefined,
): CallTrace | undefined {
  if (own !== undefined) return own;
  return typeof global === 'function' ? global() : global;
}

/**
 * The trace this attempt actually put on the wire, read back off the headers the
 * transport was handed.
 *
 * @remarks
 * Read back rather than remembered, and that is the whole point (`TR27`). On a
 * continued trace the locally minted trace id never leaves the process, so a
 * report filled from that variable names a trace no log has ever seen. These
 * three come from the `traceparent` that went out, so they name what a server
 * can be searched for.
 *
 * `undefined` when the headers carry no `traceparent`, which is the honest answer
 * for a call that traces only its request id, or does not trace at all.
 *
 * @param sent The headers the transport was called with, or `undefined` when
 *   this attempt never reached it.
 * @returns The trace, the span and the sampling decision that went out.
 */
export function sentTraceOf(
  sent: Headers | undefined,
): { sampled: boolean; spanId: string; traceId: string } | undefined {
  const parent = TRACEPARENT.exec(sent?.get('traceparent') ?? '');
  const groups = parent?.groups;
  if (groups?.traceId === undefined || groups.spanId === undefined) {
    return undefined;
  }
  return {
    sampled: sampledFrom(groups.flags),
    spanId: groups.spanId,
    traceId: groups.traceId,
  };
}

/**
 * What makes two calls the SAME trace, for the coalescing key.
 *
 * The request id and the trace id, and deliberately NOT the span id: a span is
 * per call, minted fresh even when two callers continue one trace, so keying on
 * it would split per SPAN where `TR15` asks for per TRACE. Two callers inside
 * one trace asking the same question should still share one request -- the
 * trace then shows one request, which is what actually happened.
 *
 * `tracestate` is left out for the same reason it is passed on unread: it
 * belongs to vendors between us and says nothing about which trace this is.
 */
export function traceIdentityOf(
  traced: Record<string, string>,
  inherited: boolean,
): string {
  const parent = TRACEPARENT.exec(traced.traceparent ?? '');
  // A request id counts only when it came from UPSTREAM. One this library minted
  // is the `callId` and therefore unique per call, so counting it would make
  // `perTrace` mean "never share" in `trace: 'request-id'` -- a mode with no
  // trace in it at all, where `false` shares (TR15).
  const requestId = inherited ? traced['x-request-id'] ?? '' : '';
  return `${requestId}|${parent?.groups?.traceId ?? ''}`;
}

/**
 * What a `before` handler is told about the call it is looking at (`TR23`).
 *
 * Read from the options as they stand BEFORE the handlers, which is the whole
 * point: a handler may replace `requestId` or `trace`, and then the caller's own
 * answer is gone from everything downstream. Nothing here comes from the wire,
 * because at this point nothing has reached it.
 */
export function beforeTraceOf(
  options: RequestOptions,
  callId: string,
): BeforeTrace {
  const askedTrace = options.trace;
  return Object.freeze({
    callId,
    // A mode is a boolean or a string; only an object is a context to continue.
    //
    // COPIED before it is frozen, and both halves matter. Frozen, because a
    // handler that writes to what it was handed may not change what the next
    // reader sees. Copied, because `askedTrace` is the CALLER's own object:
    // freezing it in place would reach back into their data and stop them from
    // reusing it on a second call.
    //
    // SHALLOW, which covers every field `TraceContext` declares, since all of
    // them are primitives. `TraceContext` is a structural interface, though, and
    // excess-property checks only apply to object literals -- so a caller who
    // passes a variable carrying an extra object-valued field keeps that field
    // mutable, and two callers sharing one flight would then share it.
    ...(typeof askedTrace === 'object'
      ? { incoming: Object.freeze({ ...askedTrace }) }
      : {}),
    ...(typeof options.requestId === 'string'
      ? { requestedId: options.requestId }
      : {}),
  });
}

/**
 * What this attempt knows about itself, built at the moment it is asked (`TR31`).
 *
 * @remarks
 * Everything about the wire is read back off `sent`, the headers the transport
 * was actually handed. Nothing is remembered from a variable a previous attempt
 * set, and nothing is filled from a source this attempt may not have used: on a
 * continued trace the locally minted trace id never goes out, and a report built
 * from it would name a trace that exists in no log (`TR27`).
 *
 * That also makes absence mean one thing. An attempt that stranded before the
 * transport has no `sent`, so every wire field is missing rather than carrying
 * the previous attempt's answer.
 */
export function traceOf(
  identity: BeforeTrace,
  counted: { attempts: number; requests: number },
  sent: Headers | undefined,
  startedAt: number,
): Trace {
  const wire = sentTraceOf(sent);
  const requestId = sent?.get('x-request-id') ?? undefined;
  const traceState = sent?.get('tracestate') ?? undefined;
  // Frozen for the reason `TR34` gives: two callers on one shared flight may not
  // be able to change what the other observes. `readonly` says so to a compiler
  // and to nobody at runtime, which is where two callers actually meet.
  //
  // `incoming` came out of `beforeTraceOf` already frozen, and the spread copies
  // the reference rather than the object, so it stays that way.
  return Object.freeze({
    ...identity,
    ...counted,
    durationMs: Date.now() - startedAt,
    ...(requestId === undefined ? {} : { requestId }),
    ...(traceState === undefined ? {} : { traceState }),
    ...wire,
  });
}
